'use client';

import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn, type WithUndefined } from '@/lib/utils';

/**
 * Switch Styles Configuration
 *
 * Defines visual styles for the Switch component using design tokens.
 * States: on/off (enabled/disabled)
 */
const switchStyles = {
  root: {
    base: 'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
    on: 'bg-primary-500 hover:bg-primary-600',
    off: 'bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600',
  },
  thumb: {
    base: 'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg transition-transform',
    on: 'translate-x-5',
    off: 'translate-x-0',
  },
};

export type SwitchProps = WithUndefined<React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>> & {
  /** Label to display next to the switch */
  label?: string | undefined;
  /** Helper text to display below the switch */
  helperText?: string | undefined;
  /** If true, displays a required indicator */
  required?: boolean | undefined;
  /** Error message to display below the switch */
  error?: string | undefined;
  /** Size variant: sm, md, lg */
  size?: 'sm' | 'md' | 'lg' | undefined;
  /** Additional CSS classes */
  className?: string | undefined;
};

/**
 * Switch Component
 *
 * A fully accessible toggle switch component using Radix UI primitives.
 * Supports on/off states with smooth transitions.
 * Integrates with design tokens for light/dark mode support.
 *
 * @example
 * ```tsx
 * // Basic switch
 * <Switch checked={enabled} onCheckedChange={setEnabled} />
 *
 * // With label
 * <Switch
 *   checked={darkMode}
 *   onCheckedChange={setDarkMode}
 *   label="Dark Mode"
 * />
 *
 * // With helper text
 * <Switch
 *   checked={notifications}
 *   onCheckedChange={setNotifications}
 *   label="Enable Notifications"
 *   helperText="Receive updates about your account"
 * />
 *
 * // Disabled state
 * <Switch
 *   checked={true}
 *   label="This feature is unavailable"
 *   disabled
 * />
 *
 * // Different sizes
 * <Switch size="sm" label="Small" />
 * <Switch size="md" label="Medium" />
 * <Switch size="lg" label="Large" />
 *
 * // With error state
 * <Switch
 *   checked={false}
 *   label="Accept terms"
 *   error="You must accept the terms to continue"
 * />
 *
 * // With react-hook-form
 * <Switch
 *   label="Remember me"
 *   {...register('rememberMe')}
 * />
 * ```
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(
  (
    {
      checked,
      label,
      helperText,
      required,
      error,
      size = 'md',
      className,
      disabled,
      onCheckedChange,
      ...props
    },
    ref
  ) => {
    // Size-specific styles
    const sizeStyles = {
      sm: {
        root: 'h-5 w-9',
        thumb: 'h-4 w-4',
        thumbOn: 'translate-x-4',
      },
      md: {
        root: 'h-6 w-11',
        thumb: 'h-5 w-5',
        thumbOn: 'translate-x-5',
      },
      lg: {
        root: 'h-7 w-14',
        thumb: 'h-6 w-6',
        thumbOn: 'translate-x-7',
      },
    };

    const sizeConfig = sizeStyles[size];

    return (
      <div className="w-full">
        <div className="flex items-center gap-3">
          {/* Switch */}
          <SwitchPrimitive.Root
            ref={ref}
            checked={checked ?? false}
            {...(onCheckedChange !== undefined ? { onCheckedChange } : {})}
            disabled={disabled ?? false}
            className={cn(
              switchStyles.root.base,
              sizeConfig.root,
              checked ? switchStyles.root.on : switchStyles.root.off,
              className
            )}
            {...(props as React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>)}
          >
            <SwitchPrimitive.Thumb
              className={cn(
                switchStyles.thumb.base,
                sizeConfig.thumb,
                checked ? sizeConfig.thumbOn : switchStyles.thumb.off
              )}
            />
          </SwitchPrimitive.Root>

          {/* Label */}
          {label && (
            <label className="text-sm font-medium text-text-primary cursor-pointer hover:text-text-secondary transition-colors">
              {label}
              {required && <span className="ml-1 text-error-500">*</span>}
            </label>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="mt-1 text-sm text-error-500">
            {error}
          </p>
        )}

        {/* Helper Text */}
        {helperText && !error && (
          <p className="mt-1 text-sm text-text-secondary">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';

export { Switch };
